import { Content, Header, Page } from '@backstage/core-components';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { getSurfaceBorder, getSurfaceColor, radii } from '../../theme/tokens';
import { GreetingBanner } from './widgets/GreetingBanner';
import { MyPullRequests } from './widgets/MyPullRequests';
import { OpsGenie } from './widgets/OpsGenie';
import { QuickLinks } from './widgets/QuickLinks';
import { StarredEntitiesWidget } from './widgets/StarredEntities';

const referenceLinks = [
  {
    title: 'Getting started',
    href: '/docs/default/component/backstage/',
    description: 'Portal basics, access patterns, and troubleshooting.',
  },
  {
    title: 'Coding standards',
    href: '/docs/default/domain/coding-standards/',
    description:
      'Shared engineering standards, conventions, and implementation expectations.',
  },
  {
    title: 'Information Retrieval API Guide',
    href: '/docs/default/Domain/information-retrieval',
    description: 'Reference material for retrieval workflows and usage.',
  },
  {
    title: 'NLP guide',
    href: '/docs/default/component/fhai-nlp-schema',
    description: 'Schema guidance for Meltwater NLP integrations.',
  },
  {
    title: 'Mandatory GitHub Guidelines',
    href: '/docs/default/component/github/#mandatory-guidelines',
    description: 'Required engineering guardrails for repository work.',
  },
  {
    title: 'GitHub: All Things',
    href: '/docs/default/component/github',
    description: 'Tooling, workflows, and standards used across Meltwater R&D.',
  },
];

const supportLinks = [
  {
    title: '#backstage',
    href: 'https://meltwater.slack.com/archives/C025FS9DN80',
    description: 'Questions, feedback, and platform support.',
  },
  {
    title: 'Engineering Enablement wiki',
    href: 'https://meltwater.atlassian.net/wiki/spaces/EE/overview',
    description: 'Team-owned knowledge, process, and enablement material.',
  },
  {
    title: 'Engineering Teams wiki',
    href: 'https://meltwater.atlassian.net/wiki/spaces/ENG/pages/2212102262/Engineering+Teams',
    description: 'Org references and team-level documentation.',
  },
  {
    title: 'Search the portal',
    href: '/search',
    description:
      'Search across software, TechDocs, and catalog entities from one place.',
  },
];

type LinkRowProps = {
  description: string;
  href: string;
  title: string;
};

const isExternalLink = (href: string) => /^https?:\/\//.test(href);

const LinkRow = ({ description, href, title }: LinkRowProps) => {
  const external = isExternalLink(href);

  return (
    <Link
      color="inherit"
      component={external ? 'a' : RouterLink}
      href={external ? href : undefined}
      rel={external ? 'noreferrer' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        py: 1.75,
        textDecoration: 'none',
      }}
      target={external ? '_blank' : undefined}
      to={external ? undefined : href}
      underline="none"
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ lineHeight: 1.35 }} variant="subtitle2">
          {title}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ lineHeight: 1.55, mt: 0.25 }}
          variant="body2"
        >
          {description}
        </Typography>
      </Box>
      {external ? (
        <LaunchRoundedIcon
          sx={{ color: 'text.disabled', fontSize: 18, flexShrink: 0 }}
        />
      ) : (
        <ChevronRightIcon
          sx={{ color: 'text.disabled', fontSize: 20, flexShrink: 0 }}
        />
      )}
    </Link>
  );
};

const GuideTile = ({ description, href, title }: LinkRowProps) => {
  const theme = useTheme();
  const external = isExternalLink(href);

  return (
    <Link
      color="inherit"
      component={external ? 'a' : RouterLink}
      href={external ? href : undefined}
      rel={external ? 'noreferrer' : undefined}
      sx={{
        borderRadius: `${radii.md}px`,
        color: 'inherit',
        display: 'block',
        height: '100%',
        minWidth: 0,
        textDecoration: 'none',
        '&:hover .HomePage-guideTile, &:focus-visible .HomePage-guideTile': {
          backgroundColor: getSurfaceColor(theme, 'subtle'),
          borderColor: getSurfaceBorder(theme, 'strong'),
        },
      }}
      target={external ? '_blank' : undefined}
      to={external ? undefined : href}
      underline="none"
    >
      <Box
        className="HomePage-guideTile"
        sx={{
          alignItems: 'center',
          backgroundColor: getSurfaceColor(theme, 'raised'),
          border: `1px solid ${getSurfaceBorder(theme)}`,
          borderRadius: `${radii.md}px`,
          display: 'flex',
          gap: 2,
          height: '100%',
          px: 2.5,
          py: 2,
          transition: 'background-color 150ms ease, border-color 150ms ease',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ lineHeight: 1.35 }} variant="subtitle2">
            {title}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ lineHeight: 1.55, mt: 0.5 }}
            variant="body2"
          >
            {description}
          </Typography>
        </Box>
        <ChevronRightIcon
          sx={{ color: 'text.disabled', fontSize: 22, flexShrink: 0 }}
        />
      </Box>
    </Link>
  );
};

export const HomePage = () => {
  const theme = useTheme();

  return (
    <Page themeId="home">
      <Header
        title="Home"
        subtitle="Developer platform, documentation, and self-service workflows."
      />
      <Content>
        <Box sx={{ width: '100%', px: 3 }}>
          <Stack spacing={3}>
            <GreetingBanner />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '8fr 4fr' },
                gap: 3,
              }}
            >
              {/* Main content column */}
              <Stack spacing={3}>
                <MyPullRequests />

                <Paper
                  sx={{
                    px: 3.5,
                    py: 3.5,
                  }}
                >
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="h6">Guides and standards</Typography>
                      <Typography
                        color="text.secondary"
                        sx={{ lineHeight: 1.55, mt: 0.75 }}
                        variant="body2"
                      >
                        The documentation and platform references teams most
                        often need first.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 3,
                      }}
                    >
                      {referenceLinks.map(link => (
                        <GuideTile key={link.title} {...link} />
                      ))}
                    </Box>
                  </Stack>
                </Paper>
              </Stack>

              {/* Sidebar column */}
              <Stack spacing={3}>
                <OpsGenie />
                <QuickLinks />
                <StarredEntitiesWidget />
                <Paper
                  sx={{
                    px: 3,
                    py: 3,
                  }}
                >
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="h6">
                        Support and resources
                      </Typography>
                      <Typography
                        color="text.secondary"
                        sx={{ lineHeight: 1.55, mt: 0.75 }}
                        variant="body2"
                      >
                        Platform channels, org references, and shared knowledge
                        bases.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: getSurfaceColor(theme, 'subtle'),
                        border: `1px solid ${getSurfaceBorder(theme)}`,
                        borderRadius: `${radii.lg}px`,
                        px: 3,
                        py: 0.5,
                      }}
                    >
                      <Stack divider={<Divider flexItem />} spacing={0}>
                        {supportLinks.map(link => (
                          <LinkRow key={link.title} {...link} />
                        ))}
                      </Stack>
                    </Box>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{
                        alignItems: 'center',
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.035,
                        ),
                        border: `1px solid ${getSurfaceBorder(theme)}`,
                        borderRadius: `${radii.md}px`,
                        px: 2.5,
                        py: 2,
                      }}
                    >
                      <Box
                        sx={{
                          alignItems: 'center',
                          backgroundColor: getSurfaceColor(theme, 'raised'),
                          border: `1px solid ${getSurfaceBorder(theme)}`,
                          borderRadius: `${radii.sm}px`,
                          color: 'primary.main',
                          display: 'flex',
                          flexShrink: 0,
                          height: 40,
                          justifyContent: 'center',
                          width: 40,
                        }}
                      >
                        <ExtensionOutlinedIcon fontSize="small" />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2">
                          Need API details?
                        </Typography>
                        <Typography
                          color="text.secondary"
                          sx={{ lineHeight: 1.5, mt: 0.25 }}
                          variant="body2"
                        >
                          Browse published definitions and service contracts.
                        </Typography>
                      </Box>
                      <Button
                        component={RouterLink}
                        size="small"
                        to="/api-docs"
                        variant="outlined"
                      >
                        APIs
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Content>
    </Page>
  );
};
